/**
 * Home screen calendar — thin client over the dispatch-portal calendar endpoint.
 *
 * TEMPORARY: the full dispatch-portal client lands on its own branch; when it
 * merges, delete this folder and repoint `mapCalendarEvents.ts` at those types.
 * The URL path stays `/v1/servicedesk/dispatch-portal/calendar` — only the
 * frontend folder is namespaced `home-calendar` to avoid a merge collision.
 *
 * `msp_id` and the current user are derived from the JWT — never sent.
 */

import type { AxiosError, AxiosResponse } from "axios";
import type { HomeCalendarData, HomeCalendarQuery } from "shared";
import { axiosClient } from "utilz";
import { apiErrorHandler, generateUrlString } from "utilz/helpers";

const BASE_URL = "/v1/servicedesk/dispatch-portal";

/**
 * GET `/v1/servicedesk/dispatch-portal/calendar`
 *
 * Array params go as CSV (`resources=id1,id2`) — the backend does not accept a
 * JSON-stringified array.
 */
export const getHomeCalendar = async ({
  start,
  end,
  calendar_type = "team-member",
  resources,
  boards,
  recurring_duration_minutes,
}: HomeCalendarQuery): Promise<HomeCalendarData> => {
  try {
    const data: Record<string, string> = { start, end, calendar_type };
    if (resources?.length) {
      data.resources = resources.join(",");
    }
    if (boards?.length) {
      data.boards = boards.join(",");
    }
    if (recurring_duration_minutes) {
      data.recurring_duration_minutes = String(recurring_duration_minutes);
    }

    const url = await generateUrlString({
      baseUrl: `${BASE_URL}/calendar`,
      data,
    });
    const response = await axiosClient.get(url);
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData.data as HomeCalendarData;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
